import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Printer, ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { CONTACT, ZEFFY_SPIKE } from "@/constants/config";
import { api } from "@/lib/api";

const SPIKE_PHOTO_FALLBACK =
  "https://customer-assets.emergentagent.com/job_bulldog-sanctuary/artifacts/ju5pf1q8_3ffea0d1-cdaf-4f27-bafc-68de2dbc5be7.webp";

function useVariant() {
  const { campaign } = useParams();
  const [spikeDog, setSpikeDog] = useState(null);
  const isSpike = campaign === "spike";

  useEffect(() => {
    if (!isSpike) return;
    api.get("/dogs")
      .then((r) => {
        const s = (r.data || []).find((d) => d.name?.toLowerCase() === "spike");
        if (s) setSpikeDog(s);
      })
      .catch(() => {});
  }, [isSpike]);

  if (!isSpike) {
    return {
      eyebrow: "Kōkua • Give with Aloha",
      headline: (
        <>
          Every Paw Deserves a<br />Second Chance at a Lavish Life.
        </>
      ),
      copy: "Scan the QR to donate directly through Zeffy — 100% of your gift funds vet care, transport from Hawaiʻi, food, and safe fosters. Zero platform fees.",
      qrTargetPath: "/donate",
      qrUrl: (origin) => `${origin}/donate`,
      photo: null,
      filename: "lavish808-donate-qr.png",
      docTitle: "Donate Flyer — Lavish 808 Bulliez Rescued Paws",
    };
  }

  return {
    eyebrow: "Urgent Campaign • Mālama Spike",
    headline: (
      <>
        Help Fund Spike's<br />Eye Surgery.
      </>
    ),
    copy:
      spikeDog?.story?.slice(0, 260) ||
      "Spike came to us with a severe eye condition that requires urgent surgery. Help us give this sweet boy the sight and second chance he deserves — 100% of your gift funds his care through Zeffy.",
    qrTargetPath: "/donate",
    qrUrl: () => ZEFFY_SPIKE,
    photo: spikeDog?.image_url || SPIKE_PHOTO_FALLBACK,
    filename: "lavish808-spike-surgery-qr.png",
    docTitle: "Spike's Eye Surgery — Donate Flyer",
    urgent: true,
  };
}

export default function QRFlyer() {
  const canvasWrapRef = useRef(null);
  const variant = useVariant();
  const [qrValue, setQrValue] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setQrValue(variant.qrUrl(window.location.origin));
    }
    document.title = variant.docTitle;
  }, [variant]);

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = variant.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printFlyer = () => window.print();

  return (
    <div className="qr-flyer-root" style={{ backgroundColor: "#F7ECD9", minHeight: "100vh" }}>
      <style>{`
        @media print {
          @page { size: letter portrait; margin: 0.4in; }
          body { background: #ffffff !important; }
          .no-print { display: none !important; }
          .qr-flyer-root { background: #ffffff !important; }
          .flyer-card {
            box-shadow: none !important;
            border: 2px solid #0E5C6E !important;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="no-print sticky top-0 z-40 border-b" style={{ backgroundColor: "#FFFFFF", borderColor: "#0E5C6E22" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/donate" className="inline-flex items-center gap-2 text-sm" style={{ color: "#0E5C6E" }} data-testid="flyer-back-link">
            <ArrowLeft size={16} /> Back to Donate
          </Link>
          <div className="flex flex-wrap gap-3">
            <button onClick={downloadPng} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-colors"
              style={{ borderColor: "#0E5C6E", color: "#0E5C6E", backgroundColor: "white" }}
              data-testid="flyer-download-qr">
              <Download size={16} /> Download QR only
            </button>
            <button onClick={printFlyer} className="btn-donate" data-testid="flyer-print">
              <Printer size={16} /> Print flyer
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div
          className="flyer-card bg-white rounded-3xl overflow-hidden"
          style={{ boxShadow: "0 10px 40px rgba(14,92,110,0.15)" }}
          data-testid="flyer-card"
        >
          {/* Header band */}
          <div className="px-8 py-6 flex items-center gap-4" style={{ backgroundColor: "#0E5C6E", color: "#ffffff" }}>
            <img
              src="https://customer-assets.emergentagent.com/job_bulldog-sanctuary/artifacts/g4vq3c1t_1782224774424.png"
              alt="Lavish 808 Bulliez Rescued Paws"
              className="w-16 h-16 object-contain rounded-full bg-white p-1"
            />
            <div className="leading-tight">
              <div className="font-serif text-2xl">Lavish 808 Bulliez</div>
              <div className="text-[0.7rem] tracking-[0.3em] uppercase text-white/80">Rescued Paws • Saving Since 2024</div>
            </div>
            <div className="ml-auto text-right text-[0.65rem] uppercase tracking-[0.25em] text-white/70">
              501(c)(3)<br/>EIN {CONTACT.ein}
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-10 text-center">
            {variant.photo && (
              <div className="mx-auto mb-6 w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden"
                style={{ border: "4px solid #E27E4E" }}>
                <img src={variant.photo} alt="Spike" className="w-full h-full object-cover" data-testid="flyer-photo" />
              </div>
            )}

            <div className="text-xs uppercase tracking-[0.35em] mb-3"
              style={{ color: variant.urgent ? "#C56439" : "#E27E4E" }}>
              {variant.eyebrow}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mb-3" style={{ color: "#0E5C6E" }} data-testid="flyer-headline">
              {variant.headline}
            </h1>
            <p className="text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed" style={{ color: "#2D2B2A" }}>
              {variant.copy}
            </p>

            <div className="flex justify-center mb-8">
              <div
                ref={canvasWrapRef}
                className="bg-white p-6 rounded-2xl"
                style={{ border: "3px solid #0E5C6E" }}
                data-testid="flyer-qr-canvas"
              >
                {qrValue && (
                  <QRCodeCanvas
                    value={qrValue}
                    size={280}
                    bgColor="#FFFFFF"
                    fgColor="#0E5C6E"
                    level="H"
                    includeMargin={false}
                  />
                )}
              </div>
            </div>

            <div className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: "#E27E4E" }}>
              {variant.urgent ? "Scan • Give • Save Spike's Sight" : "Scan • Donate • Save a Life"}
            </div>
            <div className="text-xs break-all mb-8" style={{ color: "#6B655E" }} data-testid="flyer-donate-url">{qrValue}</div>

            <div
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 mt-4 border-t text-sm"
              style={{ borderColor: "#0E5C6E22", color: "#2D2B2A" }}
            >
              <div>
                <div className="text-[0.65rem] uppercase tracking-[0.25em] mb-1" style={{ color: "#E27E4E" }}>Call</div>
                <div className="font-serif">{CONTACT.phone}</div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-[0.25em] mb-1" style={{ color: "#E27E4E" }}>Email</div>
                <div className="font-serif text-xs break-all">{CONTACT.email}</div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-[0.25em] mb-1" style={{ color: "#E27E4E" }}>Follow</div>
                <div className="font-serif">@lavish808buliezrescuedpaws</div>
              </div>
            </div>
          </div>

          <div className="px-8 py-4 text-center text-xs" style={{ backgroundColor: "#F7ECD9", color: "#6B655E" }}>
            <span className="font-serif italic">"Saving Lives Through Aloha, Compassion & Community"</span> • Est. 2026 • {CONTACT.address}
          </div>
        </div>

        <div className="no-print text-center mt-6 text-xs" style={{ color: "#6B655E" }}>
          Tip: Use your browser's <strong>Print</strong> option and select "Save as PDF" to email or share digitally.
        </div>
      </div>
    </div>
  );
}
